import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Phone, Calendar, Clock, ArrowLeftRight, Bot, TrendingUp } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, tid }) {
  return (
    <div data-testid={tid} className="bg-white border border-slate-200 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="mt-3 text-3xl font-bold font-heading tracking-tight text-slate-950">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function TrialRecap() {
  const [r, setR] = useState(null);
  const [biz, setBiz] = useState(null);
  useEffect(() => {
    api.get("/reports/trial-recap").then(x => setR(x.data));
    api.get("/business").then(x => setBiz(x.data)).catch(() => {});
  }, []);
  if (!r) return <div className="p-8 text-slate-500">Loading…</div>;
  const active = biz?.subscription_status === "active";

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Your ASFA Results</h1>
          <p className="text-slate-600 mt-1">What ASFA has done for your business so far.</p>
        </div>
        {active ? (
          <div className="text-sm text-slate-600 flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span data-testid="recap-plan-badge"><b className="capitalize">{(biz.plan || "paid").replace("_monthly", "")}</b> plan · active</span>
          </div>
        ) : (
          <div className="text-sm text-slate-600 flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <b data-testid="recap-trial-days">{r.trial_days_left}</b>&nbsp;days left in free trial
          </div>
        )}
      </div>

      <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-8">
        <div className="text-xs uppercase tracking-wider font-semibold opacity-80">Estimated appointment revenue</div>
        <div className="mt-2 text-5xl font-bold font-heading tracking-tight">${r.estimated_appointment_revenue.toFixed(2)}</div>
        <div className="mt-2 text-sm opacity-90">in AI-booked appointments during your trial · ~{r.estimated_hours_saved} hours of receptionist work saved</div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Calls answered" value={r.total_calls} icon={Phone} tid="recap-calls" sub={`${r.outside_hours_calls} outside business hours`} />
        <Stat label="Appointments booked" value={r.appointments_booked} icon={Calendar} tid="recap-appts" sub={`${r.ai_appointments_booked} booked by AI`} />
        <Stat label="Rescheduled" value={r.appointments_rescheduled} icon={ArrowLeftRight} tid="recap-resched" />
        <Stat label="Human transfers" value={r.human_transfers} icon={Bot} tid="recap-transfers" />
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Cancelled" value={r.appointments_cancelled} icon={Calendar} tid="recap-cancelled" />
        <Stat label="Avg call duration" value={`${r.avg_call_duration_sec}s`} icon={Clock} tid="recap-avgdur" />
        <Stat label="Hours saved" value={`${r.estimated_hours_saved}h`} icon={TrendingUp} tid="recap-hours" sub="~3.5 min per call (estimate)" />
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 text-center">
        {active ? (
          <>
            <div className="font-heading font-semibold text-lg">You're subscribed — ASFA stays live for your business.</div>
            <div className="text-sm text-slate-600 mt-1">Manage your plan, payment method, or invoices anytime in Settings.</div>
            <Link to="/app/settings"><Button data-testid="recap-manage-btn" className="mt-4 rounded-xl bg-blue-600 hover:bg-blue-700">Manage subscription</Button></Link>
          </>
        ) : (
          <>
            <div className="font-heading font-semibold text-lg">Ready to keep this going?</div>
            <div className="text-sm text-slate-600 mt-1">When your trial ends on {r.trial_end ? new Date(r.trial_end).toLocaleDateString() : "—"}, plans start at $99.99/month.</div>
            <Link to="/app/settings"><Button data-testid="recap-upgrade-btn" className="mt-4 rounded-xl bg-blue-600 hover:bg-blue-700">See plans</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
